/**
 * Content → Pages → Checks, publishing and history in the person's words
 * ("tinycms UX optimization review v12", Prototype v7): versions by number
 * ("v3 working · v2 live", no hash; the hash stays in history), checks by
 * their plain names, and the two checks the page can fix in place.
 */

export interface VersionState {
  live?: { version: number } | null;
  scheduled?: { version: number } | null;
  hasUnpublishedChanges?: boolean;
  /** The working copy's version: the live one while unchanged, else the next number. */
  workingVersion?: number | null;
}

/** The working copy and what readers see, by number. Null when there's nothing to say. */
export function versionLine(s: VersionState): string | null {
  const w = s.workingVersion || null;
  const live = s.live?.version;
  if (s.scheduled) {
    return live
      ? `v${s.scheduled.version} scheduled · v${live} live`
      : `v${s.scheduled.version} scheduled`;
  }
  if (live)
    return s.hasUnpublishedChanges && w && w !== live
      ? `v${w} working · v${live} live`
      : `v${live} live`;
  if (!w) return null;
  return w > 1 ? `v${w} · not live` : `v${w} · never published`;
}

/** "v3", for the publish question; "this version" when the number isn't known. */
export const versionName = (s: VersionState) =>
  s.workingVersion ? `v${s.workingVersion}` : 'this version';

export interface CheckFinding {
  code: string;
  severity: 'blocking' | 'warning';
  message?: string;
  label?: string;
  formId?: string;
  formName?: string | null;
}

export type CheckFix = 'publish-form' | 'share-image';

/** The fix a check offers inline: publish the named form with the page, or pick a share image. */
export function checkFix(f: CheckFinding): CheckFix | null {
  if (f.code === 'form_not_live' && f.formId && f.formName) return 'publish-form';
  if (f.code === 'share_image_missing') return 'share-image';
  return null;
}

/** The forms "Publish the form with this page" takes along: each named one that isn't live. */
export function formsToPublish(findings: CheckFinding[]): { id: string; name: string }[] {
  const out = new Map<string, string>();
  for (const f of findings) {
    if (checkFix(f) === 'publish-form') out.set(f.formId as string, f.formName as string);
  }
  return [...out].map(([id, name]) => ({ id, name }));
}

/**
 * Blocking checks the form publish doesn't clear. While any remain, publishing
 * the form with the page would be refused, so the button waits for them.
 */
export const otherBlocking = (findings: CheckFinding[], copyEditMissing = 0) =>
  findings.filter((f) => f.severity === 'blocking' && checkFix(f) !== 'publish-form').length +
  copyEditMissing;

/** "the Tips form", "The Briefing form", "the Tips and The Briefing forms". */
export function formsPhrase(forms: { name: string }[]): string {
  const names = forms.map((f) => f.name.trim().replace(/\s+form$/i, ''));
  const list = andList(names);
  const article = /^the\s/i.test(names[0]) ? '' : 'the ';
  return `${article}${list} ${names.length === 1 ? 'form' : 'forms'}`;
}

/** "Home", "Home and About", "Home, About and Tips". */
export function andList(names: string[]): string {
  if (names.length < 2) return names[0] || '';
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}
